import { AppBar, Box, Stack, ThemeProvider, Toolbar } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import BurgerMenu from './components/BurgerMenu';
import CompanySwitcher from './components/CompanySwitcher';
import MenuNavLinks from './components/MenuNavLinks';
import { useAuth } from 'contexts/ProtectedRoute';

import DOMAIN from 'assets/domains.json';
// Same assets as pre-revamp Header (not the public/ domainBranding paths)
import { ReactComponent as AliroLogoMinimal } from 'assets/images/aliro-logo-m.svg';
import { ReactComponent as AliroLogo } from 'assets/images/aliro-logo.svg';
import ROLE from 'assets/userRoles.json';
import { DomainKey } from 'data/constants/domainApiKey';
import useEnabledCountries from 'hooks/useEnabledCountries';
import useTranslate from 'hooks/useTranslate';
import { loadApplications } from 'redux/applications/applications-middleware';
import { AppDispatch, RootState } from 'redux/store';
import { MainLink, RegisteredRoute } from 'types';
import { ACCOUNT_HOME, selfServeEntryPath } from 'utils/countries';
import { getDomainBranding } from 'utils/domainBranding';
import appThemeV2 from 'theme/themeV2';

/** Single compact height for mobile + desktop */
const headerHeight = 56;
/** Fills most of the header bar while leaving a little vertical padding */
const logoHeight = 44;
const fullLogoMaxWidth = 200;
const minimalLogoMaxWidth = 52;

/** Shared with UserDashboardV4 calendar booking flow */
export interface Agent {
    company_name?: string | null;
    country: string;
    expertise?: string[];
    id?: number;
    incorporated: boolean;
    language: string[];
    long_description: string;
    region: string;
    short_description: string;
    super_admin_id: number;
    super_admin_name: string;
    target_country?: string[];
}

export const agent: Agent = {
    company_name: '',
    country: '',
    expertise: [],
    id: 0,
    incorporated: false,
    language: [],
    long_description: '',
    region: '',
    short_description: '',
    super_admin_id: 0,
    super_admin_name: '',
    target_country: [],
};

const Header: React.FC = () => {
    const [mainLinks, setMainLinks] = useState<MainLink[]>([]);
    const { firstname: userFirstName, lastname: userLastName } = useSelector(
        (state: RootState) => state.user.entity
    );
    const { applications } = useSelector((state: RootState) => state.applications);
    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const applicationsUserId = selectedUserId || user.id;
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const { role } = useAuth();
    const { translate } = useTranslate();
    const { codes, hasCanada, hasSelfServeMarket } = useEnabledCountries();
    const selfServeDashboard = selfServeEntryPath(codes);
    const visaProcessLinkRoles = hasSelfServeMarket
        ? [ROLE.completedAssessment, ROLE.externalClient]
        : hasCanada
          ? [ROLE.externalClient]
          : [];

    const registeredRoutes: RegisteredRoute[] = [
        ...(selfServeDashboard
            ? [
                  {
                      name: translate('dashboard'),
                      path: selfServeDashboard,
                      allowedRoles: [ROLE.completedAssessment, ROLE.academicConsultation],
                  },
              ]
            : []),
        {
            name: translate('dashboard'),
            path: '/dashboard',
            allowedRoles: [
                ROLE.sales,
                ROLE.admin,
                ROLE.superAdmin,
                ROLE.superSuperAdmin,
                ROLE.consultant,
                ROLE.backofficeAdmin,
                ROLE.backofficeConsultant,
            ],
        },
        {
            name: translate('onboarding'),
            path: '/dashboard/sales-onboarding',
            allowedRoles: [
                ROLE.sales,
                ROLE.admin,
                ROLE.superAdmin,
                ROLE.superSuperAdmin,
                ROLE.consultant,
                ROLE.backofficeAdmin,
                ROLE.backofficeConsultant,
            ],
        },
        {
            name: translate('home'),
            path: '/visa-process',
            nestedRoutes: [],
            allowedRoles: visaProcessLinkRoles,
        },
        {
            name: translate('analysis'),
            path: '/analysis',
            allowedRoles: [],
        },
    ];

    useEffect(() => {
        getRoutesAccordingToRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, codes, hasCanada, hasSelfServeMarket]);

    useEffect(() => {
        if (
            [ROLE.completedAssessment, ROLE.academicConsultation, ROLE.externalClient].includes(
                user.role
            ) &&
            applicationsUserId
        ) {
            dispatch(loadApplications(applicationsUserId.toString()));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationsUserId, user.role]);

    useEffect(() => {
        const allowedLinks = getAllowedLinks();
        if (applications.length > 0) {
            const updatedLinks = allowedLinks.map((link) => {
                if (link.name.toLowerCase() === translate('home').toLowerCase()) {
                    return {
                        ...link,
                        path: `visa-process/1/agreement`,
                        nestedRoutes: [],
                    };
                }
                return link;
            });
            prepareMainLinks(updatedLinks);
        } else {
            const updatedLinks = allowedLinks.map((link) => {
                if (link.name.toLowerCase() === translate('home').toLowerCase()) {
                    return {
                        ...link,
                        path: `${link.path}/empty`,
                        nestedRoutes: [],
                    };
                }
                return link;
            });
            prepareMainLinks(updatedLinks);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications, codes, hasCanada, hasSelfServeMarket]);

    const getAllowedLinks = () =>
        registeredRoutes.filter((link) => link.allowedRoles.includes(user.role));

    const prepareMainLinks = (allowedLinks: RegisteredRoute[]) => {
        const links = allowedLinks.map<MainLink>((link) => ({
            name: link.name,
            path: link.path,
            nestedLinks: link.nestedRoutes ? link.nestedRoutes : [],
        }));
        setMainLinks(links);
    };

    const getRoutesAccordingToRole = () => {
        prepareMainLinks(getAllowedLinks());
    };

    const isUnqualified = role === ROLE.unqualified;
    const isVisaCandidate = role === ROLE.completedAssessment || role === ROLE.academicConsultation;
    const selfServeLogo = isUnqualified
        ? selfServeEntryPath(codes, 'unqualified')
        : isVisaCandidate
          ? selfServeEntryPath(codes)
          : null;
    const logoPath = selfServeLogo ?? (isVisaCandidate || isUnqualified ? ACCOUNT_HOME : '');

    const branding = getDomainBranding();

    // Domains that used custom img logos (same set as pre-revamp Header.styled map).
    // Default / Aliro domains use embedded SVG components from assets/.
    const partnerLogoDomains = new Set<string>([
        DOMAIN.MEDIACRUSH,
        DOMAIN.BASTIN,
        DOMAIN.LOOKNOFURTHER,
        DOMAIN.UISAUSTRALIA,
        DOMAIN.UIS_AUSTRALIA,
        DOMAIN.TEAMSTAFFINGINTERNATIONAL,
        DOMAIN.MDCPORTAL,
        DOMAIN.RESOLVED,
        DOMAIN.GLOBALCITIZENS,
        DOMAIN.GREATNORTHVISA,
        DOMAIN.JOSHUASLAYEN,
        DOMAIN.UISUK,
        DOMAIN.BIZBUDDY,
        DOMAIN.NEWDAYIMMIGRATION,
        DOMAIN.LOCAL,
        DOMAIN.AUSSIEMIGRATE,
        DOMAIN.SANDBOX,
        DOMAIN.EASY,
        DOMAIN.ALIROAUSTRALIA,
        DOMAIN.ALIROCANADA,
        DOMAIN.ALIROUK,
    ]);
    const isDefaultAliroLogo = !partnerLogoDomains.has(DomainKey);

    // Original breakpoints: full logo above xl (1280); some partners used sm (768)
    const logoSwitchAtSm = new Set<string>([
        DOMAIN.UISAUSTRALIA,
        DOMAIN.UIS_AUSTRALIA,
        DOMAIN.GLOBALCITIZENS,
        DOMAIN.GREATNORTHVISA,
        DOMAIN.JOSHUASLAYEN,
        DOMAIN.UISUK,
        DOMAIN.BIZBUDDY,
        DOMAIN.NEWDAYIMMIGRATION,
    ]);

    const useSmLogoBreakpoint = logoSwitchAtSm.has(DomainKey);
    const fullLogoSx = useSmLogoBreakpoint
        ? {
              display: 'block',
              '@media (max-width: 767px)': { display: 'none' },
          }
        : {
              display: 'block',
              '@media (max-width: 1279px)': { display: 'none' },
          };

    const minimalLogoSx = useSmLogoBreakpoint
        ? {
              display: 'none',
              '@media (max-width: 767px)': { display: 'block' },
          }
        : {
              display: 'none',
              '@media (max-width: 1279px)': { display: 'block' },
          };

    const displayName = [userFirstName, userLastName]
        .filter((part) => typeof part === 'string' && part.trim().length > 0)
        .join(' ')
        .trim();

    return (
        <ThemeProvider theme={appThemeV2}>
            <AppBar
                position="fixed"
                color="inherit"
                elevation={0}
                component="header"
                sx={{
                    height: headerHeight,
                    minHeight: headerHeight,
                    bgcolor: 'background.paper',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 0,
                    zIndex: (theme) => theme.zIndex.appBar,
                }}
            >
                <Toolbar
                    disableGutters
                    variant="dense"
                    sx={{
                        width: '100%',
                        maxWidth: 1600,
                        mx: 'auto',
                        minHeight: headerHeight,
                        height: headerHeight,
                        px: { xs: 1.5, sm: 2.5, md: 3.5 },
                        // Mobile: logo + actions; desktop: 3-col grid keeps nav truly centered
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'auto 1fr',
                            sm: '1fr auto 1fr',
                        },
                        alignItems: 'center',
                        columnGap: { xs: 1, sm: 1.5, md: 2 },
                    }}
                >
                    {/* Brand logo */}
                    <Box
                        component={NavLink}
                        to={logoPath}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            lineHeight: 0,
                            textDecoration: 'none',
                            justifySelf: 'start',
                        }}
                    >
                        {isDefaultAliroLogo ? (
                            <>
                                <Box
                                    sx={{
                                        ...fullLogoSx,
                                        lineHeight: 0,
                                        '& svg': {
                                            height: logoHeight,
                                            width: 'auto',
                                            maxWidth: fullLogoMaxWidth,
                                            display: 'block',
                                        },
                                    }}
                                >
                                    <AliroLogo />
                                </Box>
                                <Box
                                    sx={{
                                        ...minimalLogoSx,
                                        lineHeight: 0,
                                        '& svg': {
                                            height: logoHeight,
                                            width: minimalLogoMaxWidth,
                                            display: 'block',
                                        },
                                    }}
                                >
                                    <AliroLogoMinimal />
                                </Box>
                            </>
                        ) : (
                            <>
                                <Box
                                    component="img"
                                    src={branding.logoHeader}
                                    alt={translate('logo')}
                                    sx={{
                                        ...fullLogoSx,
                                        height: logoHeight,
                                        width: 'auto',
                                        maxWidth: fullLogoMaxWidth,
                                        objectFit: 'contain',
                                        objectPosition: 'left center',
                                    }}
                                />
                                <Box
                                    component="img"
                                    src={branding.logoMinimal}
                                    alt={translate('logo')}
                                    sx={{
                                        ...minimalLogoSx,
                                        height: logoHeight,
                                        width: 'auto',
                                        maxWidth: minimalLogoMaxWidth,
                                        objectFit: 'contain',
                                        objectPosition: 'left center',
                                    }}
                                />
                            </>
                        )}
                    </Box>

                    {/* Primary nav — desktop only; mobile uses BurgerMenu */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        <MenuNavLinks links={mainLinks} />
                    </Box>

                    <Stack
                        direction="row"
                        sx={{ alignItems: 'center', justifySelf: 'end', gap: 1 }}
                    >
                        {user.role === ROLE.superSuperAdmin && <CompanySwitcher />}

                        {user.role.length > 0 && (
                            <BurgerMenu
                                userName={displayName}
                                links={mainLinks}
                                location={location}
                                userRole={user.role}
                            />
                        )}
                    </Stack>
                </Toolbar>
            </AppBar>
        </ThemeProvider>
    );
};

export default Header;
